"use client";

import { useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import { SLIDE_W, SLIDE_H, type CoverSlideData } from "@/lib/carousel/types";
import { coverTextLayout, type CoverTextBox } from "@/lib/carousel/templates/field-diary";

export interface FieldDiaryCoverOverlayProps {
  data: CoverSlideData;
  fonts: { display: string; body: string };
  update: (patch: Partial<{ kickerText: string; headlineTitle: string; subtitle: string }>) => void;
}

/** Mirrors the canvas's textBaseline="middle" by centering the input on the
 * same `middle` y — see coverTextLayout() for why boxes carry a middle. */
function boxStyle(box: CoverTextBox, fonts: { display: string; body: string }): CSSProperties {
  return {
    position: "absolute",
    left: box.left,
    top: box.middle,
    transform: "translateY(-50%)",
    width: box.width,
    textAlign: "left",
    fontFamily: box.family === "display" ? fonts.display : fonts.body,
    fontSize: box.fontSize,
    fontWeight: box.fontWeight,
    color: box.color,
    letterSpacing: box.tracking ? `${box.tracking}px` : undefined,
    textTransform: box.uppercase ? "uppercase" : undefined,
  };
}

/** Same pattern as FieldDiarySpeciesOverlay/FieldDiaryClosingOverlay, for the
 * cover's kicker/headline/subtitle — positioned via coverTextLayout(), the
 * same source of truth field-diary.ts's drawCover uses. The headline wraps
 * to up to 2 lines like the canvas does (clipped past that, matching the
 * canvas's own truncation — it never grows a 3rd line either). */
export default function FieldDiaryCoverOverlay({ data, fonts, update }: FieldDiaryCoverOverlayProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(360 / SLIDE_W);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const measure = () => {
      if (el.clientWidth > 0) setScale(el.clientWidth / SLIDE_W);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const { kicker, headline, subtitle } = coverTextLayout(data, fonts);

  return (
    <div ref={wrapRef} className="ced-text-overlay">
      <div
        className="ced-text-overlay-inner"
        style={{ width: SLIDE_W, height: SLIDE_H, transform: `scale(${scale})` }}
      >
        <input
          type="text"
          className="ced-text-overlay-input"
          style={boxStyle(kicker, fonts)}
          value={data.kickerText}
          onChange={(e) => update({ kickerText: e.target.value })}
          placeholder="Kicker"
          aria-label="Kicker"
        />
        <textarea
          className="ced-text-overlay-input ced-text-overlay-textarea"
          style={{
            ...boxStyle(headline, fonts),
            height: headline.lineHeight * headline.lineCount,
            lineHeight: `${headline.lineHeight}px`,
          }}
          value={data.headlineTitle}
          onChange={(e) => update({ headlineTitle: e.target.value })}
          onKeyDown={(e) => { if (e.key === "Enter") e.preventDefault(); }}
          placeholder="Headline"
          aria-label="Headline"
          rows={1}
        />
        <input
          type="text"
          className="ced-text-overlay-input"
          style={boxStyle(subtitle, fonts)}
          value={data.subtitle}
          onChange={(e) => update({ subtitle: e.target.value })}
          placeholder="Subtitle"
          aria-label="Subtitle"
        />
      </div>
    </div>
  );
}
